import axios from 'axios';

// API configuration
const API_BASE_URL = process.env.NODE_ENV === 'development'
  ? '/api'
  : (process.env.REACT_APP_API_URL || 'https://unitrade-nexus.onrender.com/api');

// Create an axios instance for API calls
const api = axios.create({
  baseURL: API_BASE_URL
});

// Use an interceptor to add the auth token to every request
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('sessionToken') || localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Helper function to get session token
const getSessionToken = () => {
  return localStorage.getItem('sessionToken') || localStorage.getItem('token');
};

/**
 * Login user with email and password
 * @param {string} email - User email
 * @param {string} password - User password
 * @returns {Promise<Object>} - User object
 */
export const loginUser = async (email, password) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/login`, { email, password });
    if (response.data.success) {
      localStorage.setItem('sessionToken', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      localStorage.setItem('userRole', response.data.user.role || '');
      return response.data.user;
    } else {
      throw new Error(response.data.message || 'Login failed');
    }
  } catch (error) {
    console.error('Error in loginUser:', error);
    
    // Extract error message and provide more user-friendly messages
    let errorMessage = 'An error occurred during login.';
    
    if (error.response) {
      // Server responded with error
      const statusCode = error.response.status;
      const serverMessage = error.response.data?.message;
      
      if (statusCode === 401 || statusCode === 403) {
        errorMessage = 'Invalid credentials. Please check your email and password.';
      } else if (statusCode === 404) {
        errorMessage = 'Login service unavailable. Please try again later.';
      } else if (serverMessage) {
        errorMessage = serverMessage;
      }
    } else if (error.request) {
      // Request made but no response received
      errorMessage = 'Unable to connect to the server. Please check your internet connection and try again.';
    } else if (error.message) {
      // Something else caused the error
      if (error.message.toLowerCase().includes('network error')) {
        errorMessage = 'Network error. Please check your internet connection and try again.';
      } else if (error.message.toLowerCase().includes('timeout')) {
        errorMessage = 'Request timed out. Please try again later.';
      } else {
        errorMessage = error.message;
      }
    }
    
    throw new Error(errorMessage);
  }
};

/**
 * Logout user and clear session data
 * @returns {Promise<void>}
 */
export const logoutUser = async () => {
  try {
    const token = getSessionToken();
    if (token) {
      // Inform the backend about logout for session invalidation
      await axios.post(`${API_BASE_URL}/logout`, {}, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
    }
  } catch (error) {
    console.error('Error during logout:', error);
  } finally {
    // Always clear local storage regardless of API success
    localStorage.removeItem('sessionToken');
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('userRole');
  }
};

/**
 * Get current logged in user from local storage
 * @returns {Object|null} - User object or null if not logged in
 */
export const getCurrentUser = () => {
  try {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      return JSON.parse(userStr);
    }
    return null;
  } catch (err) {
    console.error('Error getting current user:', err);
    return null;
  }
};

/**
 * Update user profile information
 * @param {Object} userData - User data to update
 * @returns {Promise<Object>} - Updated user object
 */
export const updateUserProfile = async (userData) => {
  try {
    const response = await api.put('/users/profile', userData);
    if (response.data.success) {
      // Update local storage with new user data
      localStorage.setItem('user', JSON.stringify(response.data.user));
      return response.data.user;
    }
    throw new Error(response.data.message || 'Failed to update profile');
  } catch (err) {
    console.error('Error updating user profile:', err);
    throw new Error(err.response?.data?.message || 'An error occurred while updating your profile');
  }
};

/**
 * Register a new user
 * @param {Object} userData - User registration data
 * @returns {Promise<Object>} - Registration response
 */
export const registerUser = async (userData) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/register`, userData);
    if (response.data.success) {
      return response.data;
    }
    throw new Error(response.data.message || 'Registration failed');
  } catch (error) {
    console.error('Error in registerUser:', error);
    
    // Extract error message
    let errorMessage = 'An error occurred during registration.';
    
    if (error.response?.data?.message) {
      errorMessage = error.response.data.message;
    } else if (error.message) {
      errorMessage = error.message;
    }
    
    if (errorMessage.includes('email verification')) {
      throw new Error('Your email verification is invalid or has expired. Please try again.');
    }
    throw new Error(errorMessage);
  }
};

/**
 * Send OTP to user email
 * @param {string} email - User email
 * @returns {Promise<Object>} - OTP response
 */
export const sendOtp = async (email) => {
  try {
    const response = await api.post('/send-otp', { email });
    return response.data;
  } catch (err) {
    const errorMessage = err.response?.data?.message || 'Failed to send OTP';
    console.error('Error in sendOtp:', errorMessage);
    throw new Error(errorMessage);
  }
};

/**
 * Verify OTP
 * @param {string} email - User email
 * @param {string} otp - OTP code
 * @returns {Promise<Object>} - Verification response
 */
export const verifyOtp = async (email, otp) => {
  try {
    const response = await api.post('/verify-otp', { email, otp });
    return response.data;
  } catch (err) {
    const errorMessage = err.response?.data?.message || 'OTP verification failed';
    console.error('Error in verifyOtp:', errorMessage);
    throw new Error(errorMessage);
  }
};

/**
 * Send password reset email
 * @param {string} email - User email
 * @returns {Promise<Object>} - Response
 */
export const sendPasswordResetEmail = async (email) => {
  try {
    const response = await api.post('/forgot-password', { email });
    return response.data;
  } catch (error) {
    console.error('Error sending password reset email:', error);
    throw new Error(error.response?.data?.message || 'An error occurred while sending the password reset email.');
  }
};
